import { useMemo } from 'react';
import { styled } from 'styled-components';
import { Choice, ChoiceItem } from '@ingka/choice';
import Text from '@ingka/text';
import { useTranslation } from 'react-i18next';
import type { AvailableService } from '../services/appointment/model';
import { useStateDispatch, useStateSelector } from '../store';
import { updateSelectedService } from '../reducers/app/appReducer';
import { mediaQueries } from '../styles/mediaQueries';
import { getServiceLocationKey } from '../helpers/appHelper';
import { shouldHidePriceZero } from '../helpers/currencyHelper';
import ServicePrice from './ServicePrice';
import { translationExists } from '../helpers/translationsHelper';
import Loading from './Loading';

const ChoiceItemContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueries.portraitTabletUp} {
    flex-direction: row;
  }
`;

const ChoiceItemLeftContent = styled.div`
  flex: 1 1 auto;
  word-break: break-word;
  hyphens: auto;
  -moz-hyphens: auto;
  -webkit-hyphens: auto;
`;

const ChoiceItemRightContent = styled.div`
  flex: 1 0 auto;
  margin-top: 12px;

  ${mediaQueries.portraitTabletUp} {
    margin-left: 1.5rem;
    margin-top: 0;
    justify-content: flex-end;
  }
`;

const TextAllowingNewLines = styled(Text)`
  &&& {
    white-space: pre-line;
  }
`;

const ListOfServices: React.FC<{
  services: AvailableService[];
  isBusy?: boolean;
}> = ({ services, isBusy }) => {
  const { t } = useTranslation();
  const dispatch = useStateDispatch();
  const selectedServiceId = useStateSelector(
    (state) => state.app.selected.serviceId,
  );
  const isLoading = useStateSelector((state) => state.services.isLoading);

  const getPreselected = (): number | undefined => {
    if (selectedServiceId) {
      const index = services.findIndex(
        (service) => service.serviceId === selectedServiceId,
      );
      return index > -1 ? index : undefined;
    }
    return undefined;
  };

  const serviceDescriptions = useMemo((): Map<string, string> => {
    return services.reduce((acc, service): Map<string, string> => {
      const description = translationExists(
        `services.infoText.${service.serviceId}`,
        t,
      )
        ? t(`services.infoText.${service.serviceId}`)
        : '';
      if (description) {
        acc.set(service.serviceId, description);
      }
      return acc;
    }, new Map<string, string>());
  }, [services, t]);

  return (
    <>
      {isLoading && <Loading title={t('components.loading.title.services')} />}
      <Choice preselectedIndex={getPreselected()}>
        {services.map(({ serviceId, serviceCode, location, displayPrice }) => {
          const serviceLocation = getServiceLocationKey(location);
          return (
            <ChoiceItem
              disabled={isBusy}
              onClick={() =>
                dispatch(
                  updateSelectedService({
                    serviceId,
                    serviceCode,
                  }),
                )
              }
              role="group"
              aria-labelledby="pages.services.title"
              key={serviceId}
              id={serviceId}
              customContent={
                <ChoiceItemContainer>
                  <ChoiceItemLeftContent>
                    <Text textStyle="Label.M" className="choice-item__title">
                      {t(`services.name.${serviceId}`)}
                    </Text>
                    <Text
                      textStyle="Caption.M"
                      className="choice-item__caption"
                    >
                      {t(`components.servicePicker.${serviceLocation}`)}
                    </Text>
                  </ChoiceItemLeftContent>
                  <ChoiceItemRightContent>
                    {!shouldHidePriceZero(
                      displayPrice.priceOfferType,
                      displayPrice.exclSavings,
                      CSS_CONFIG,
                    ) && (
                      <ServicePrice
                        price={displayPrice}
                        serviceId={serviceId}
                      />
                    )}
                  </ChoiceItemRightContent>
                </ChoiceItemContainer>
              }
              valueAlignment="top"
              data-cy={serviceId}
              data-cy-location-key={serviceLocation}
            >
              {serviceDescriptions.has(serviceId) && (
                <TextAllowingNewLines tagName="span">
                  {serviceDescriptions.get(serviceId)}
                </TextAllowingNewLines>
              )}
            </ChoiceItem>
          );
        })}
      </Choice>
    </>
  );
};

export default ListOfServices;
